/**
 * Загрузка данных — страница для того, кто эти данные присылает.
 *
 * Он отправляет пакет и получает код ответа. Дальше пакет разбирается,
 * сверяется и может не пройти — и об этом ему больше никто не сообщит.
 * Здесь один ответ на его единственный вопрос: всё ли доехало, а если
 * нет, то что именно чинить.
 */

import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '../api'
import type {
  IngestAttempt,
  BatchReport,
  CurrentUser,
  DictionaryCatalog,
  GrossProfitCheck,
  IngestProgress,
  StreamTotals,
} from '../types'
import { GrossProfitCheckView } from '../components/GrossProfitCheck'
import { Badge, Card, Empty, Loading, Notice, TableWrap } from '../components/ui'
import { ColumnChart } from '../finance/charts'
import { monthLabel, shortMonth } from '../finance/period'
import '../finance/finance.css'
import { formatDateTime, formatMoney, formatMonth, formatNumber } from '../format'

const CATALOG_TITLES: Record<string, string> = {
  organization: 'Организации',
  department: 'Подразделения',
  warehouse: 'Склады',
  counterparty: 'Контрагенты',
  contract: 'Договоры',
  nomenclature: 'Номенклатура',
  employee: 'Сотрудники и пользователи',
  currency: 'Валюты',
}

const STREAM_TITLES: Record<string, string> = {
  dictionaries: 'Справочники',
  sales: 'Реализации',
  purchases: 'Закупки',
  cashflow: 'Движение денег',
  settlements: 'Взаиморасчёты',
  stock_balance: 'Остатки',
  stock_moves: 'Движения товаров',
  gl_turnover: 'Обороты по счетам',
  budget: 'Бюджеты',
  payroll: 'ФОТ',
}

/**
 * Сколько прошло с последнего пакета.
 *
 * Молчащая ночью выгрузка выглядит в кабинете ровно как работающая:
 * цифры на месте, статусы зелёные. Возраст последнего пакета —
 * единственное, что эту разницу показывает.
 */
function describeAge(iso: string): string {
  const hours = Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000)

  if (hours < 1) return 'меньше часа назад'
  if (hours < 24) return `${hours} ч назад`

  const days = Math.floor(hours / 24)
  return `${days} дн назад — давно не приходило`
}

/** Сколько месяцев итогов видно сразу; остальные — по кнопке. */
const MONTHS_SHOWN = 12

export function IngestStatus({ user }: { user: CurrentUser }) {
  const [progress, setProgress] = useState<IngestProgress | null>(null)
  const [batches, setBatches] = useState<BatchReport[]>([])
  const [problems, setProblems] = useState<BatchReport[]>([])
  const [attempts, setAttempts] = useState<IngestAttempt[]>([])
  const [catalogs, setCatalogs] = useState<DictionaryCatalog[]>([])
  const [grossChecks, setGrossChecks] = useState<GrossProfitCheck[]>([])
  const [database, setDatabase] = useState<string | null>(null)
  const [totals, setTotals] = useState<StreamTotals | null>(null)
  const [totalsStream, setTotalsStream] = useState('sales')
  const [allMonths, setAllMonths] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [updatedAt, setUpdatedAt] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const progressResult = await api.ingestProgress(database ?? undefined)

      // Проблемные пакеты — отдельной выборкой за месяц, а не «последние N».
      // Иначе одна ночь первичной загрузки вытесняла вчерашние падения,
      // и «провалов нет» означало лишь «их не видно».
      const [batchesResult, problemsResult, catalogsResult, totalsResult, attemptsResult, checksResult] = await Promise.all([
        api.etlBatches(30, progressResult.database_id),
        api.etlBatches(200, progressResult.database_id, true),
        api.ingestDictionaries(),
        api.ingestTotals(totalsStream, progressResult.database_id),
        api.ingestAttempts(progressResult.database_id),
        // Сверка Excel — подсказка, а не основа кабинета: её сбой не должен
        // прятать всё остальное.
        api.grossProfitChecks(5).catch(() => ({ checks: [] as GrossProfitCheck[] })),
      ])
      setGrossChecks(checksResult.checks)
      setAttempts(attemptsResult.attempts)
      setProgress(progressResult)
      setBatches(batchesResult.batches)
      setProblems(problemsResult.batches)
      setCatalogs(catalogsResult.catalogs)
      setTotals(totalsResult)
      setUpdatedAt(new Date().toISOString())
      setError(null)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Не удалось загрузить состояние')
    }
  }, [database, totalsStream])

  useEffect(() => {
    void load()

    // Обновление само: человек отправляет пакет и смотрит сюда, разбор
    // занимает до минуты. Заставлять его жать F5 — значит заставлять
    // сомневаться, обновилось ли вообще.
    const timer = setInterval(() => void load(), 20_000)
    return () => clearInterval(timer)
  }, [load])

  if (error && !progress) return <Notice tone="bad">{error}</Notice>
  // Скелетон той же композиции: строка состояния, «Что загружено», итоги по месяцам.
  if (!progress)
    return (
      <div className="stack" style={{ gap: 18 }} aria-busy="true" aria-label="Загрузка">
        <div className="skeleton" style={{ height: 40, borderRadius: 'var(--radius)' }} />
        <Card title="Что загружено" flush>
          <Loading rows={6} />
        </Card>
        <Card title="Сколько чего легло" flush>
          <Loading rows={6} />
        </Card>
      </div>
    )

  // Непринятый пакет, после которого тот же период загружен, — история,
  // а не открытая проблема: чинить нечего, данные на месте.
  const failed = problems.filter((batch) => batch.status === 'failed' && !batch.superseded_by)
  const superseded = problems.filter((batch) => batch.status === 'failed' && batch.superseded_by)
  // Обратная связь к блоку повторов: у пакета видно, что под его uid
  // потом приходили другие отправки и ничего не записали.
  const reuse = new Map<string, { repeats: number; rejected: number }>()
  for (const attempt of attempts) {
    if (!attempt.batch_uid || attempt.outcome === 'invalid') continue
    const entry = reuse.get(attempt.batch_uid) ?? { repeats: 0, rejected: 0 }
    if (attempt.outcome === 'repeat') entry.repeats += attempt.count
    else entry.rejected += attempt.count
    reuse.set(attempt.batch_uid, entry)
  }
  const warned = problems.filter((batch) => batch.status !== 'failed' && batch.warnings.length > 0)
  // «Что-то уже приезжало» определяется по факту пакетов, а не по месяцам:
  // у справочников календарной оси нет, и по месяцам они всегда нули.
  const started = progress.streams.filter((stream) => stream.last_received_at !== null)
  const missingRequired = catalogs.filter((catalog) => catalog.required && catalog.loaded === 0)

  return (
    <div className="stack" style={{ gap: 18 }}>
      {failed.length > 0 ? (
        <Notice tone="bad">
          <strong>Есть пакеты, которые не загрузились: {failed.length}.</strong> Данные из них в систему
          не попали. Причина у каждого написана ниже — исправьте и отправьте заново.
          {user.support_contact && (
            <>
              {' '}Если причина непонятна, напишите разработчику:{' '}
              <a href={`mailto:${user.support_contact}?subject=Сводка: пакет не загрузился`}>
                {user.support_contact}
              </a>
              .
            </>
          )}
        </Notice>
      ) : progress.backfill_complete ? (
        <Notice tone="plain" icon="check">
          <strong>Вся история загружена.</strong> Дальше присылайте только изменения по заданиям
          из <code>/ingest/state</code>.
        </Notice>
      ) : missingRequired.length > 0 ? (
        <Notice tone="warn">
          <strong>
            Чтобы открыть загрузку документов, пришлите{' '}
            {missingRequired.map((catalog) => CATALOG_TITLES[catalog.catalog] ?? catalog.catalog).join(', ').toLowerCase()}.
          </strong>{' '}
          Без подразделений у выручки не будет филиала, без номенклатуры — товарной аналитики,
          без складов — остатков. Остальные справочники уже приняты, присылать их заново не нужно.
        </Notice>
      ) : started.length === 0 ? (
        <Notice tone="info">
          Данных пока нет. Начните со справочников: пока они не загружены, задания по документам
          не выдаются.
        </Notice>
      ) : (
        <Notice tone="info">
          Загрузка идёт. Ниже видно, сколько месяцев уже принято по каждому потоку и что осталось.
        </Notice>
      )}

      <Card
        title={
          <div>
            <h3>Что загружено</h3>
            <div className="tiny muted">
              База: <code>{progress.database_id}</code>
              {updatedAt ? ` · обновлено ${formatDateTime(updatedAt)}, обновляется само` : ''}
            </div>
          </div>
        }
        actions={
          progress.databases && progress.databases.length > 1 ? (
            <select
              className="select"
              value={progress.database_id}
              onChange={(event) => setDatabase(event.target.value)}
            >
              {progress.databases.map((item) => (
                <option key={item} value={item}>
                  база: {item}
                </option>
              ))}
            </select>
          ) : undefined
        }
        flush
      >
        <TableWrap cards>
          <table className="data">
            <thead>
              <tr>
                <th>Поток</th>
                <th style={{ width: 220 }}>История</th>
                <th style={{ textAlign: 'right' }}>Осталось догрузить</th>
                <th>Последний пакет</th>
              </tr>
            </thead>
            <tbody>
              {progress.streams.map((stream) => (
                <tr key={stream.stream}>
                  <td>
                    <div>{STREAM_TITLES[stream.stream] ?? stream.stream}</div>
                    <div className="tiny muted mono">{stream.stream}</div>
                  </td>
                  <td>
                    {stream.not_expected ? (
                      // Требование, которое невозможно выполнить, хуже
                      // отсутствия требования.
                      <Badge>источника нет, присылать не нужно</Badge>
                    ) : stream.blocked_reason ? (
                      <Badge tone="warn">ждёт справочников</Badge>
                    ) : !stream.periodic ? (
                      // У справочников нет периода: они либо присланы, либо нет.
                      // Но «присланы» при отсутствии нужных справочников
                      // противоречило бы соседним строкам, которые их ждут.
                      stream.missing_catalogs.length > 0 ? (
                        <Badge tone="warn">
                          не хватает {stream.missing_catalogs.length} из обязательных
                        </Badge>
                      ) : (
                        <Badge tone={stream.last_received_at ? 'good' : undefined}>
                          {stream.last_received_at ? 'присланы' : 'не присылались'}
                        </Badge>
                      )
                    ) : stream.backfill_complete ? (
                      <Badge tone="good">загружена целиком</Badge>
                    ) : (
                      <div className="stack" style={{ gap: 4 }}>
                        <div className="progress">
                          <div className="progress__fill" style={{ transform: `scaleX(${Math.max(0, Math.min(100, stream.percent)) / 100})` }} />
                        </div>
                        <span className="tiny muted">
                          {stream.months_covered} из {stream.months_total} мес. ({stream.percent}%)
                        </span>
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {stream.not_expected ? (
                      '—'
                    ) : !stream.periodic && stream.missing_catalogs.length > 0 ? (
                      stream.missing_catalogs.length
                    ) : stream.backlog_requests > 0 ? (
                      stream.backlog_requests
                    ) : (
                      // Постоянные задания — ежедневное обновление справочников,
                      // скользящее окно, пересмотр месяца — висят всегда
                      // и долгом не являются.
                      <span className="tiny muted">всё догружено</span>
                    )}
                  </td>
                  <td className="tiny muted">
                    {stream.last_received_at ? (
                      <>
                        {formatDateTime(stream.last_received_at)}
                        <div>{describeAge(stream.last_received_at)}</div>
                      </>
                    ) : (
                      'не было'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      </Card>

      <Card
        title={
          <div>
            <h3>Сколько чего легло</h3>
            <div className="tiny muted">
              Сверьте с отчётом в 1С за тот же месяц. «Погашено строк» — документы,
              снятые пометкой на удаление или распроведением
            </div>
          </div>
        }
        actions={
          totals && totals.streams.length > 0 ? (
            <select className="select" value={totalsStream} onChange={(e) => setTotalsStream(e.target.value)}>
              {totals.streams.map((item) => (
                <option key={item} value={item}>
                  {STREAM_TITLES[item] ?? item}
                </option>
              ))}
            </select>
          ) : undefined
        }
        flush
      >
        {!totals?.totals || totals.totals.months.length === 0 ? (
          <div style={{ padding: 16 }}>
            <Empty title="Данных по этому потоку ещё нет" hint="Появятся после первой загрузки" />
          </div>
        ) : (
          <>
          <MonthStrip totals={totals.totals} />
          <TableWrap cards>
            <table className="data">
              <thead>
                <tr>
                  <th>Месяц</th>
                  {totals.totals.columns.map((column) => (
                    <th key={column.key} style={{ textAlign: 'right' }}>
                      {column.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(allMonths ? totals.totals.months : totals.totals.months.slice(0, MONTHS_SHOWN)).map((month) => (
                  <tr key={month.period}>
                    <td>
                      {formatMonth(month.period)}
                      {month.empty && (
                        <span className="tiny muted" style={{ marginLeft: 8 }}>
                          пусто
                        </span>
                      )}
                    </td>
                    {totals.totals!.columns.map((column) => {
                      const value = month.values[column.key]
                      return (
                        <td key={column.key} style={{ textAlign: 'right' }}>
                          {value === null || value === undefined
                            ? '—'
                            : column.type === 'money'
                              ? formatMoney(value)
                              : formatNumber(value)}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
          {/* 37 месяцев карточками — 23 000 px на телефоне: сначала последние двенадцать. */}
          {totals.totals.months.length > MONTHS_SHOWN && (
            <div className="panel__more">
              <button type="button" className="link-button" onClick={() => setAllMonths((value) => !value)} aria-expanded={allMonths}>
                {allMonths ? 'Свернуть до 12 месяцев' : `Показать все ${totals.totals.months.length} мес.`}
              </button>
            </div>
          )}
          </>
        )}
      </Card>

      <Card
        title={
          <div>
            <h3>Сверка с отчётом «Валовая прибыль» в 1С</h3>
            <div className="tiny muted">
              Итоги выгрузки отчёта из 1С против того, что легло в систему за тот же месяц. Разница
              по каждой паре — не больше допуска, иначе «не сошлось»
            </div>
          </div>
        }
      >
        {grossChecks.length === 0 ? (
          <Empty
            title="Сверок ещё не было"
            hint="Администратор загружает выгрузку отчёта «Валовая прибыль» на странице «Загрузка Excel» с указанием месяца — результат появится здесь"
          />
        ) : (
          <div className="stack" style={{ gap: 12 }}>
            <GrossProfitCheckView check={grossChecks[0]!} />
            {grossChecks.length > 1 && (
              <div className="tiny muted">
                Раньше:{' '}
                {grossChecks.slice(1).map((check, index) => (
                  <span key={check.id}>
                    {index > 0 ? ' · ' : ''}
                    {formatMonth(check.period.from)}, {check.source_database_id} — {check.is_ok ? 'сошлось' : 'не сошлось'}{' '}
                    ({formatDateTime(check.checked_at)})
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      <AttemptsCard attempts={attempts} />

      {superseded.length > 0 && (
        <div className="tiny muted">
          Ещё {superseded.length} непринятых пакетов за месяц уже заменены последующими: тот же поток за тот же
          период загружен позже. Исправлять ничего не нужно — они видны в «Последних пакетах» серым.
        </div>
      )}

      {failed.length > 0 && (
        <Card title="Не загрузилось — нужно исправить" flush>
          <TableWrap cards>
            <table className="data">
              <thead>
                <tr>
                  <th>Пакет</th>
                  <th>Что не так</th>
                </tr>
              </thead>
              <tbody>
                {failed.map((batch) => (
                  <tr key={batch.batch_uid}>
                    <td>
                      <div>{STREAM_TITLES[batch.stream] ?? batch.stream}</div>
                      <div className="tiny muted">
                        {batch.period ? `${batch.period.from} — ${batch.period.to}` : batch.mode}
                      </div>
                      <div className="tiny muted mono">{batch.batch_uid}</div>
                    </td>
                    <td>
                      <div>{batch.error ?? 'Разбор не удался'}</div>
                      {batch.reconciliation
                        .filter((check) => !check.is_ok)
                        .map((check) => (
                          <div key={check.metric} className="tiny muted">
                            {check.metric}: вы прислали {check.declared}, у нас получилось {check.ours}
                          </div>
                        ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </Card>
      )}

      <Card
        title={
          <div>
            <h3>Замечания</h3>
            <div className="tiny muted">
              Пакеты загрузились. Красное — стоит починить, серое — просто
              к сведению, делать ничего не нужно
            </div>
          </div>
        }
        flush
      >
        {warned.length === 0 ? (
          <div style={{ padding: 16 }}>
            <Empty icon="check" title="Замечаний нет" hint="Последние пакеты разобрались без оговорок" />
          </div>
        ) : (
          <TableWrap cards>
            <table className="data">
              <thead>
                <tr>
                  <th>Пакет</th>
                  <th>Замечание</th>
                  <th style={{ textAlign: 'right' }}>Что делать</th>
                </tr>
              </thead>
              <tbody>
                {warned.flatMap((batch) =>
                  batch.warnings.map((warning) => (
                    <tr key={`${batch.batch_uid}:${warning.code}`}>
                      <td>
                        <div>{STREAM_TITLES[batch.stream] ?? batch.stream}</div>
                        <div className="tiny muted">
                          {batch.period ? `${batch.period.from} — ${batch.period.to}` : batch.mode}
                        </div>
                      </td>
                      <td>
                        {warning.level === 'info' ? (
                          <span className="muted">{warning.message}</span>
                        ) : (
                          warning.message
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {warning.level === 'fix' ? (
                          <Badge tone="bad">починить</Badge>
                        ) : warning.level === 'info' ? (
                          <span className="tiny muted">к сведению</span>
                        ) : (
                          <Badge tone="warn">проверить</Badge>
                        )}
                      </td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      <Card
        title={
          <div>
            <h3>Что лежит в справочниках</h3>
            <div className="tiny muted">
              «Только в документах» — записи, на которые ссылаются документы, но которых
              нет в присланном справочнике. Обычно их отсёк отбор при выгрузке справочника,
              реже GUID в документе не совпадает. Найдите их в 1С по GUID и пришлите
              справочником — отдельным пакетом, остальное слать заново не нужно
            </div>
          </div>
        }
        flush
      >
        {catalogs.every((catalog) => catalog.loaded === 0) ? (
          <div style={{ padding: 16 }}>
            <Empty title="Справочники пусты" hint="Начните с них: без справочников задания по документам не выдаются" />
          </div>
        ) : (
          <TableWrap cards>
            <table className="data">
              <thead>
                <tr>
                  <th>Справочник</th>
                  <th>Статус</th>
                  <th style={{ textAlign: 'right' }}>Из справочника</th>
                  <th style={{ textAlign: 'right' }}>Из них папок</th>
                  <th style={{ textAlign: 'right' }}>Только в документах</th>
                  <th>Например</th>
                </tr>
              </thead>
              <tbody>
                {[...catalogs]
                  .sort((a, b) => {
                    // Непришедшие обязательные — первыми: ради них сюда и зашли.
                    const weight = (item: DictionaryCatalog) =>
                      item.required && item.loaded === 0 ? 0 : item.loaded > 0 ? 1 : 2
                    return weight(a) - weight(b) || b.loaded - a.loaded
                  })
                  .map((catalog) => (
                  <tr key={catalog.catalog}>
                    <td>
                      <div>{CATALOG_TITLES[catalog.catalog] ?? catalog.catalog}</div>
                      <div className="tiny muted mono">{catalog.catalog}</div>
                    </td>
                    <td>
                      {catalog.required && catalog.loaded === 0 ? (
                        <Badge tone="bad">обязателен, не прислан</Badge>
                      ) : catalog.loaded > 0 ? (
                        <Badge tone="good">принят</Badge>
                      ) : (
                        <span className="tiny muted">не присылали</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>{catalog.loaded}</td>
                    <td style={{ textAlign: 'right' }}>{catalog.folders || '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      {catalog.from_documents > 0 ? (
                        // Запись, которой нет в справочнике: либо забыли
                        // прислать, либо опечатка в GUID.
                        <Badge tone="warn">{catalog.from_documents}</Badge>
                      ) : (
                        '—'
                      )}
                    </td>
                    {catalog.missing.length > 0 ? (
                      // Здесь нужны именно недостающие: последний присланный
                      // элемент рядом с жёлтой цифрой читается как её пример.
                      <td className="tiny">
                        {catalog.missing.map((item) => (
                          <div key={item.guid}>
                            {item.name || '(без наименования)'}{' '}
                            <span className="muted mono">{item.guid}</span>
                          </div>
                        ))}
                        {catalog.from_documents > catalog.missing.length && (
                          <div className="muted">
                            и ещё {catalog.from_documents - catalog.missing.length}
                          </div>
                        )}
                      </td>
                    ) : (
                      <td className="tiny muted truncate">{catalog.sample ?? '—'}</td>
                    )}
                  </tr>
                  ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      <Card title="Последние пакеты" flush>
        {batches.length === 0 ? (
          <div style={{ padding: 16 }}>
            <Empty title="Пакетов ещё не было" hint="Здесь появятся все отправки с их итогом" />
          </div>
        ) : (
          <TableWrap cards>
            <table className="data">
              <thead>
                <tr>
                  <th>Когда</th>
                  <th>Поток</th>
                  <th>batch_uid</th>
                  <th>Период</th>
                  <th style={{ textAlign: 'right' }}>Строк</th>
                  <th>Итог</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((batch) => (
                  <tr key={batch.batch_uid}>
                    <td className="tiny muted">{formatDateTime(batch.received_at)}</td>
                    <td>{STREAM_TITLES[batch.stream] ?? batch.stream}</td>
                    <td>
                      <code className="tiny" style={{ userSelect: 'all' }}>{batch.batch_uid}</code>
                      {reuse.get(batch.batch_uid) && (
                        <div className="tiny" style={{ marginTop: 2 }}>
                          <Badge tone={reuse.get(batch.batch_uid)!.rejected > 0 ? 'bad' : 'warn'}>
                            {describeReuse(reuse.get(batch.batch_uid)!)}
                          </Badge>
                        </div>
                      )}
                    </td>
                    <td className="tiny muted">
                      {batch.period ? `${batch.period.from} — ${batch.period.to}` : batch.mode}
                    </td>
                    <td style={{ textAlign: 'right' }}>{batch.rows_count ?? '—'}</td>
                    <td>
                      <Badge
                        tone={
                          batch.status === 'failed' && !batch.superseded_by
                            ? 'bad'
                            : batch.status === 'transformed'
                              ? 'good'
                              : undefined
                        }
                      >
                        {batch.verdict}
                      </Badge>
                      {batch.superseded_by && (
                        <div className="tiny muted" style={{ marginTop: 2 }}>
                          заменён пакетом <code>{batch.superseded_by.batch_uid}</code> от{' '}
                          {formatDateTime(batch.superseded_by.received_at)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      <div className="tiny muted">
        Показаны последние {batches.length} по базе <code>{progress.database_id}</code>.
        Провалы и замечания считаются за месяц целиком, независимо от этого списка.
        Полный ответ по конкретному пакету — <code>GET /ingest/batches/&#123;batch_uid&#125;</code>.
      </div>

      {user.support_contact && (
        <Card title="Связь с разработчиком">
          <p style={{ margin: 0 }}>
            Вопрос по формату, непонятная ошибка, расхождение в цифрах —{' '}
            <a href={`mailto:${user.support_contact}?subject=Сводка: вопрос по выгрузке из 1С`}>
              {user.support_contact}
            </a>
          </p>
          <p className="tiny muted" style={{ marginTop: 6, marginBottom: 0 }}>
            К письму приложите <code>batch_uid</code> пакета — по нему видно всё, что с ним произошло.
          </p>
        </Card>
      )}
    </div>
  )
}

const OUTCOME: Record<IngestAttempt['outcome'], { label: string; tone: 'warn' | 'bad' }> = {
  uid_reused: { label: '409 · uid занят', tone: 'bad' },
  invalid: { label: '400 · отвергнут', tone: 'bad' },
  repeat: { label: '200 · повтор', tone: 'warn' },
}

function describeReuse({ repeats, rejected }: { repeats: number; rejected: number }): string {
  return [
    rejected > 0 ? `под этим uid отвергнуто: ${formatNumber(rejected)}` : null,
    repeats > 0 ? `повторов: ${formatNumber(repeats)}` : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

const BATCH_STATUS: Record<string, string> = {
  transformed: 'загружен',
  failed: 'не загрузился',
  received: 'разбирается',
  parsed: 'разбирается',
}

/** «Продажи, 2026-06-01 — 2026-06-30, 5 строк, принят …, загружен» из блока details. */
function describePacket(packet: unknown): string {
  const p = (packet ?? {}) as {
    stream?: string
    mode?: string
    period?: { from: string; to: string } | null
    rows_count?: number
    received_at?: string
    status?: string
  }
  const parts = [
    STREAM_TITLES[p.stream ?? ''] ?? p.stream,
    p.period ? `${p.period.from} — ${p.period.to}` : p.mode,
    p.rows_count !== undefined ? `${formatNumber(p.rows_count)} строк` : null,
    p.received_at ? `принят ${formatDateTime(p.received_at)}` : null,
    p.status ? (BATCH_STATUS[p.status] ?? p.status) : null,
  ]
  return parts.filter(Boolean).join(', ')
}

/**
 * Отправки, которые ничего не записали.
 *
 * В списке пакетов их нет: туда попадает только то, что легло. Ответ
 * сервера обработка 1С не хранит, поэтому без этого блока отказ
 * и повтор под занятым uid не видны никому — так и ушли четыре сотни
 * отправок с новыми данными.
 */
function AttemptsCard({ attempts }: { attempts: IngestAttempt[] }) {
  const rejected = attempts.filter((attempt) => attempt.outcome !== 'repeat')

  return (
    <Card
      title={
        <div>
          <h3>Повторы и отказы на приёме</h3>
          <div className="tiny muted">
            Отправки за две недели, которые ничего не записали. В «Последних пакетах» их нет.
          </div>
        </div>
      }
      flush
    >
      {rejected.length > 0 && (
        <div style={{ padding: '12px 16px 0' }}>
          <Notice tone="bad">
            <strong>Отвергнуто отправок: {rejected.reduce((sum, a) => sum + a.count, 0)}.</strong>{' '}
            Данные из них не попали в систему. Новые данные отправляйте с новым <code>batch_uid</code>;
            прежний повторяют только для того же пакета после сбоя связи.
          </Notice>
        </div>
      )}
      {attempts.length === 0 ? (
        <Empty
          title="Повторов и отказов не было"
          hint="Каждая отправка за две недели что-то записала"
        />
      ) : (
        <TableWrap cards>
          <table className="data">
            <thead>
              <tr>
                <th>Отправка</th>
                <th>Ответ</th>
                <th style={{ textAlign: 'right' }}>Раз</th>
                <th>Что произошло</th>
              </tr>
            </thead>
            <tbody>
              {attempts.map((attempt) => {
                const details = attempt.details ?? {}
                const outcome = OUTCOME[attempt.outcome]
                return (
                  <tr key={`${attempt.batch_uid}|${attempt.outcome}|${attempt.error}|${attempt.last_at}`}>
                    <td>
                      <div>{STREAM_TITLES[attempt.stream] ?? attempt.stream}</div>
                      <div className="tiny muted mono">{attempt.batch_uid ?? 'batch_uid не указан'}</div>
                      <div className="tiny muted">
                        {attempt.count > 1
                          ? `${formatDateTime(attempt.first_at)} — ${formatDateTime(attempt.last_at)}`
                          : formatDateTime(attempt.last_at)}
                      </div>
                    </td>
                    <td>
                      <Badge tone={outcome.tone}>{outcome.label}</Badge>
                    </td>
                    <td style={{ textAlign: 'right' }}>{formatNumber(attempt.count)}</td>
                    <td>
                      {attempt.outcome === 'repeat' ? (
                        <>
                          <div>
                            Тот же пакет уже принят, повтор ничего не записал. Если это были новые
                            данные — им нужен новый <code>batch_uid</code>.
                          </div>
                          <div className="tiny muted">
                            Под этим uid загружен: {describePacket(details.already_loaded)}
                          </div>
                        </>
                      ) : attempt.outcome === 'uid_reused' ? (
                        <>
                          <div>Пакет не загружен: под этим uid уже лежит другой пакет.</div>
                          <div className="tiny muted">
                            Под этим uid загружен: {describePacket(details.already_loaded)}
                          </div>
                          <div className="tiny muted">Отвергнут: {describePacket(details.rejected_now)}</div>
                        </>
                      ) : (
                        <>
                          <div>{attempt.error}</div>
                          {Array.isArray(attempt.details) &&
                            (attempt.details as { path?: string; message?: string }[]).slice(0, 5).map((issue, index) => (
                              <div key={index} className="tiny muted">
                                <code>{issue.path}</code>: {issue.message}
                              </div>
                            ))}
                        </>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </TableWrap>
      )}
    </Card>
  )
}

/**
 * Месяцы потока столбцами: пропуск в истории виден сразу, без чтения таблицы.
 * Показываются документы — их число не зависит от сумм и цен, и пустой
 * месяц в нём пустой по-настоящему.
 */
function MonthStrip({ totals }: { totals: NonNullable<StreamTotals['totals']> }) {
  const column = totals.columns.find((item) => item.key === 'documents') ?? totals.columns[0]
  if (!column) return null
  const months = [...totals.months].sort((a, b) => a.period.localeCompare(b.period)).slice(-24)
  if (months.length < 2) return null
  const empty = months.filter((month) => month.empty).length
  return (
    <div style={{ padding: '14px 16px 6px' }}>
      <ColumnChart
        ariaLabel={`${column.title} по месяцам`}
        height={120}
        format={(value) => (column.type === 'money' ? formatMoney(value) : formatNumber(Math.round(value)))}
        series={[{ key: column.key, label: column.title, color: 'var(--series-1)' }]}
        points={months.map((month) => ({
          key: month.period,
          label: shortMonth(month.period),
          title: `${monthLabel(month.period)}${month.empty ? ' · пусто' : ''}`,
          values: [month.values[column.key] === null || month.values[column.key] === undefined ? null : Number(month.values[column.key])],
        }))}
      />
      <div className="tiny muted" style={{ marginTop: 6 }}>
        {empty > 0
          ? `Пустых месяцев: ${empty} из ${months.length}. Если в 1С за них есть документы — их нужно прислать заново.`
          : `Последние ${months.length} мес. — пустых нет.`}
      </div>
    </div>
  )
}
